'use client';

import { useEffect, useState } from 'react';

import { CookieConsentStatus, updateGAConsent } from '@hub/common/src/analytics-utils';

import { useCitOriginClient } from './CitOriginClientProvider';
import { useCookieConsentStatus } from './CookieConsentStatusProvider';
import { useGTMLoadedStatus } from './GTMLoadedStatusProvider';
import { CitOrigin } from '@hub/common/src/types/cit-public-api';

export default function CookieSettingsPage() {
  const citOrigin = useCitOriginClient();
  const isHUBPro = citOrigin === CitOrigin.HUBPRO;

  const { hasConsent, setHasConsent, consentLoaded } = useCookieConsentStatus();

  const { GTMLoaded } = useGTMLoadedStatus();

  const [localConsent, setLocalConsent] = useState<CookieConsentStatus>(hasConsent);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!consentLoaded) {
      return;
    }

    // eslint-disable-next-line
    setLocalConsent(hasConsent);
  }, [hasConsent, consentLoaded]);

  const handleCheckConsent = (e: React.ChangeEvent<HTMLInputElement>) => {
    const consent = e.target.dataset.consent as CookieConsentStatus;

    setLocalConsent(consent);
  };

  const handleSave = () => {
    if (!saveable) {
      return;
    }

    setIsSaving(true);
    setHasConsent(localConsent);

    // must update google tag manager consent status via events since google tag manager is already initialized in this context
    if (GTMLoaded) {
      updateGAConsent(localConsent);
    }

    setIsSaving(false);
  };

  const saveable = localConsent !== hasConsent;

  return (
    <div className='mx-auto flex w-full flex-col gap-6 px-2 py-5 md:w-3/4 md:px-8'>
      <div className='text-center'>
        <h1 className='text-2xl font-bold'>Cookie Policy / Settings</h1>
        <p className='mt-8 text-lg text-gray-600'>
          {isHUBPro ? (
            <>
              We use optional cookies to understand how our platform is used, improve your
              experience, and remember your preferences.
            </>
          ) : (
            <>
              CatalogIt HUB uses optional cookies to understand how our platform is used, improve
              your experience, and remember your preferences.
            </>
          )}
        </p>
      </div>

      <div
        className={`rounded-xl border border-gray-200 bg-white px-8 py-6 ${isHUBPro ? '' : 'divide-y divide-gray-200'}`}
      >
        {!isHUBPro ? (
          <div className='space-y-3 pb-6'>
            <h2 className='text-xl font-medium'>Google Analytics Cookies</h2>
            <p className='text-gray-600'>
              Google Analytics helps measure how visitors use CatalogIt HUB, including visitor
              activity and traffic patterns. This information helps CatalogIt improve the platform
              and user experience.
            </p>
            <p className='text-gray-600'>
              Organizations may also choose to provide their own Google Analytics tracking ID. When
              enabled, visitor activity associated with their HUB page is reported directly to their
              Google Analytics property, allowing them to view analytics through their Google
              Analytics dashboard.
            </p>
          </div>
        ) : null}

        <div className={`space-y-3 ${isHUBPro ? '' : 'pt-6'}`}>
          <h2 className='text-xl font-medium'>PostHog Analytics Cookies</h2>
          {isHUBPro ? (
            <>
              <p className='text-gray-600'>PostHog powers our built-in analytics dashboards.</p>
              <p className='text-gray-600'>
                These dashboards provide insights into how visitors interact with our page,
                including visitor traffic, engagement, content discovery, searches, referrals, and
                other usage trends. These analytics help us better understand our audience and how
                visitors discover and interact with our content.
              </p>
            </>
          ) : (
            <>
              <p className='text-gray-600'>
                PostHog powers CatalogIt&apos;s built-in analytics dashboards available to HUB Pro
                subscribers.
              </p>
              <p className='text-gray-600'>
                These dashboards provide insights into how visitors interact with their HUB page,
                including visitor traffic, engagement, content discovery, searches, referrals, and
                other usage trends. These analytics help organizations better understand their
                audience and how visitors discover and interact with their content.
              </p>
            </>
          )}
        </div>
      </div>

      {/* consent never loads in a theme tester preview, which has no consent to change */}
      {consentLoaded ? (
        <>
          <div className='flex justify-center gap-6'>
            <label className='flex items-center gap-2 font-medium'>
              <input
                type='radio'
                checked={localConsent === CookieConsentStatus.DENIED}
                data-consent={CookieConsentStatus.DENIED}
                onChange={handleCheckConsent}
                className='accent-[#005f91] cursor-pointer'
              />
              Reject All
            </label>
            <label className='flex items-center gap-2 font-medium'>
              <input
                type='radio'
                checked={localConsent === CookieConsentStatus.GRANTED}
                data-consent={CookieConsentStatus.GRANTED}
                onChange={handleCheckConsent}
                className='accent-[#005f91] cursor-pointer'
              />
              Accept All
            </label>
          </div>

          <button
            type='button'
            className='mx-auto rounded-md bg-[#005f91] px-6 py-2 text-xl font-medium text-white disabled:opacity-50 cursor-pointer'
            onClick={handleSave}
            disabled={!saveable || isSaving}
          >
            Save
          </button>
        </>
      ) : null}
    </div>
  );
}
